import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import TopBar from './TopBar';
import WikiChat from './WikiChat';
import { useAuth } from '../contexts/AuthContext';
import API_BASE_URL from '../config/api';
import './UrbanWiki.css';

// Lazy so react-pdf/pdfjs (~1MB) stays out of the main bundle.
const DocViewerModal = lazy(() => import('./DocViewerModal'));

// Urban Wiki — reader for the compiled bilingual research wiki + glossary.
// Admins additionally get a "Dokument" tab (the source list) and a
// "Lägg till dokument" button that opens the upload/compile flow in a modal.
// Access is gated to urban_wiki:view via ProtectedRoute.

const COPY = {
  sv: {
    eyebrow: 'Urban Wiki',
    title: 'Kunskapsbas',
    lede: 'Sammanställd och sammanlänkad forskning om hälsofrämjande stadsplanering, med en tvåspråkig ordlista.',
    tabWiki: 'Wiki', tabGlossary: 'Ordlista', tabChat: 'Fråga AI', tabDocs: 'Dokument',
    search: 'Sök…',
    related: 'Relaterat',
    empty: 'Wikin är tom än. Ladda upp ett forskningsdokument för att bygga den.',
    emptyGlossary: 'Inga termer än.',
    emptyDocs: 'Inga dokument uppladdade än.',
    selectPage: 'Välj en sida i listan.',
    add: 'Lägg till dokument',
    drop: 'Välj PDF eller ZIP att kompilera in i wikin',
    uploadBtn: 'Kompilera in i wikin',
    uploading: 'Laddar upp…', processing: 'Kompilerar med AI…',
    notesLabel: 'Observationer (motsägelser & kunskapsluckor)',
    pagesShort: 'sidor', termsShort: 'termer',
    close: 'Stäng',
    view: 'Visa',
    lang: 'EN',
  },
  en: {
    eyebrow: 'Urban Wiki',
    title: 'Knowledge base',
    lede: 'Compiled, interlinked research on health-promoting urban planning, with a bilingual glossary.',
    tabWiki: 'Wiki', tabGlossary: 'Glossary', tabChat: 'Ask AI', tabDocs: 'Documents',
    search: 'Search…',
    related: 'Related',
    empty: 'The wiki is empty. Upload a research document to build it.',
    emptyGlossary: 'No terms yet.',
    emptyDocs: 'No documents uploaded yet.',
    selectPage: 'Select a page from the list.',
    add: 'Add a document',
    drop: 'Choose a PDF or ZIP to compile into the wiki',
    uploadBtn: 'Compile into wiki',
    uploading: 'Uploading…', processing: 'Compiling with AI…',
    notesLabel: 'Notes (contradictions & gaps)',
    pagesShort: 'pages', termsShort: 'terms',
    close: 'Close',
    view: 'View',
    lang: 'SV',
  },
};

const UrbanWiki = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [lang, setLang] = useState('sv');
  const [tab, setTab] = useState('wiki');
  const [pages, setPages] = useState([]);
  const [glossary, setGlossary] = useState([]);
  const [query, setQuery] = useState('');
  const [activeSlug, setActiveSlug] = useState(null);
  const [activePage, setActivePage] = useState(null);
  const [loading, setLoading] = useState(true);

  // Admin document state (lifted so the Dokument tab + the add modal share it).
  const [docs, setDocs] = useState([]);
  const [pollId, setPollId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  // Document viewer: { url, filename, page? }
  const [viewerFile, setViewerFile] = useState(null);
  const [viewerLoading, setViewerLoading] = useState(false);

  // Below this width the reader becomes an accordion (page expands under its
  // entry) instead of the two-column list + article layout. Matches the 760px
  // breakpoint .wiki-grid uses in UrbanWiki.css.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 760px)');
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const t = COPY[lang];

  const fetchContent = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/wiki/content`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPages(data.pages || []);
        setGlossary(data.glossary || []);
      }
    } catch (err) {
      console.error('Failed to load wiki content:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchContent(); }, [fetchContent]);

  const fetchDocs = useCallback(async () => {
    if (!isAdmin) return [];
    try {
      const res = await fetch(`${API_BASE_URL}/wiki/documents`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setDocs(data.documents || []);
        return data.documents || [];
      }
    } catch (err) { console.error(err); }
    return [];
  }, [isAdmin]);

  useEffect(() => { if (isAdmin) fetchDocs(); }, [isAdmin, fetchDocs]);

  // Poll while a document is processing; refresh the wiki when it completes.
  useEffect(() => {
    if (!pollId) return;
    const iv = setInterval(async () => {
      const list = await fetchDocs();
      const d = list.find(x => x.docId === pollId);
      if (d && (d.status === 'completed' || d.status === 'error')) {
        setPollId(null);
        if (d.status === 'completed') fetchContent();
      }
    }, 3000);
    return () => clearInterval(iv);
  }, [pollId, fetchDocs, fetchContent]);

  // Load the full body when a page is selected.
  useEffect(() => {
    if (!activeSlug) { setActivePage(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/wiki/pages/${activeSlug}`, { credentials: 'include' });
        if (res.ok && !cancelled) setActivePage(await res.json());
      } catch (err) {
        console.error('Failed to load page:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [activeSlug]);

  const q = query.trim().toLowerCase();
  const shownPages = pages.filter(p =>
    !q ||
    (p[`title_${lang}`] || '').toLowerCase().includes(q) ||
    (p[`summary_${lang}`] || '').toLowerCase().includes(q));
  const shownTerms = glossary.filter(term =>
    !q ||
    (term[`term_${lang}`] || '').toLowerCase().includes(q) ||
    (term[`def_${lang}`] || '').toLowerCase().includes(q));

  const pageSlugs = new Set(pages.map(p => p.slug));
  const titleOf = (slug) => {
    const p = pages.find(pg => pg.slug === slug);
    return p ? (p[`title_${lang}`] || p.slug) : slug;
  };

  const statusLabel = (s) => ({
    pending_upload: '…', processing: t.processing, completed: '✓', error: '✕',
  }[s] || s);

  // Open a source document in the viewer, optionally at a specific page (used
  // both by the Dokument list and by chat citations).
  const openDocument = useCallback(async (docId, filename, page) => {
    setViewerLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/wiki/documents/${docId}/file`, { credentials: 'include' });
      if (!res.ok) throw new Error('could not get document url');
      const data = await res.json();
      setViewerFile({ url: data.url, filename: data.filename || filename, page });
    } catch (err) {
      console.error('Failed to open document:', err);
    } finally {
      setViewerLoading(false);
    }
  }, []);

  // Shared by both layouts (desktop right column, mobile accordion body).
  const renderArticleBody = () => (
    <>
      <h2>{activePage[`title_${lang}`] || activePage.slug}</h2>
      <div className="wiki-md">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {activePage[`body_${lang}`] || ''}
        </ReactMarkdown>
      </div>
      {(() => {
        // Only link related pages that actually exist — the compiler
        // occasionally emits a slug for a page it never created.
        const rel = (activePage.related || []).filter(s => pageSlugs.has(s));
        return rel.length > 0 && (
          <div className="wiki-related">
            <strong>{t.related}:</strong>{' '}
            {rel.map((slug, i) => (
              <button key={slug} className="wiki-link" onClick={() => setActiveSlug(slug)} data-tip="wiki.related">
                {titleOf(slug)}{i < rel.length - 1 ? ',' : ''}
              </button>
            ))}
          </div>
        );
      })()}
    </>
  );

  return (
    <>
      <TopBar />
      {/* data-tip-lang: tooltips under this root follow the page language */}
      <div className="urban-wiki ut-page" data-tip-lang={lang}>
        <div className="wiki-head-row">
          <div className="ut-page-head">
            <div className="ut-eyebrow">{t.eyebrow}</div>
            <h1>{t.title}</h1>
            <p className="ut-lede">{t.lede}</p>
          </div>
          <button className="wiki-lang" onClick={() => setLang(lang === 'sv' ? 'en' : 'sv')} data-tip="wiki.lang">
            {t.lang}
          </button>
        </div>

        {/* Tabs. The admin-only Dokument tab is last (so it is last on mobile
            too), with the "Lägg till dokument" button to its right. */}
        <div className="wiki-tabs">
          <button className={tab === 'wiki' ? 'active' : ''} onClick={() => setTab('wiki')} data-tip="wiki.tab.wiki">{t.tabWiki}</button>
          <button className={tab === 'glossary' ? 'active' : ''} onClick={() => setTab('glossary')} data-tip="wiki.tab.glossary">{t.tabGlossary}</button>
          <button className={tab === 'chat' ? 'active' : ''} onClick={() => setTab('chat')} data-tip="wiki.tab.chat">{t.tabChat}</button>
          {isAdmin && (
            <button className={`wiki-tab-docs ${tab === 'documents' ? 'active' : ''}`} onClick={() => setTab('documents')} data-tip="wiki.tab.docs">
              {t.tabDocs}{docs.length > 0 ? ` (${docs.length})` : ''}
            </button>
          )}
          {isAdmin && (
            <button className="wiki-add-btn" onClick={() => setShowAdd(true)} data-tip="wiki.add">+ {t.add}</button>
          )}
          {tab !== 'documents' && tab !== 'chat' && (
            <input
              className="wiki-search"
              type="search"
              data-tip="wiki.search"
              placeholder={t.search}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          )}
        </div>

        {loading ? (
          <p className="wiki-muted">…</p>
        ) : tab === 'wiki' ? (
          pages.length === 0 ? (
            <p className="wiki-muted">{t.empty}</p>
          ) : isMobile ? (
            /* Mobile: accordion — the page expands directly under its entry,
               and tapping the open entry collapses it again. */
            <div className="wiki-list">
              {shownPages.map(p => {
                const open = activeSlug === p.slug;
                return (
                  <div key={p.slug} className="wiki-acc">
                    <button
                      className={`wiki-list-item ${open ? 'active' : ''}`}
                      onClick={() => setActiveSlug(open ? null : p.slug)}
                      aria-expanded={open}
                    >
                      <span className="wiki-list-title">{p[`title_${lang}`] || p.slug}</span>
                      <span className="wiki-list-sum">{p[`summary_${lang}`]}</span>
                    </button>
                    {open && (
                      <article className="wiki-page wiki-page-inline ut-paper r1">
                        {activePage && activePage.slug === p.slug
                          ? renderArticleBody()
                          : <p className="wiki-muted">…</p>}
                      </article>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="wiki-grid">
              <nav className="wiki-list">
                {shownPages.map(p => (
                  <button
                    key={p.slug}
                    className={`wiki-list-item ${activeSlug === p.slug ? 'active' : ''}`}
                    onClick={() => setActiveSlug(p.slug)}
                  >
                    <span className="wiki-list-title">{p[`title_${lang}`] || p.slug}</span>
                    <span className="wiki-list-sum">{p[`summary_${lang}`]}</span>
                  </button>
                ))}
              </nav>
              <article className="wiki-page ut-paper r1">
                {activePage ? renderArticleBody() : <p className="wiki-muted">{t.selectPage}</p>}
              </article>
            </div>
          )
        ) : tab === 'glossary' ? (
          glossary.length === 0 ? (
            <p className="wiki-muted">{t.emptyGlossary}</p>
          ) : (
            <div className="wiki-glossary">
              {shownTerms.map(term => (
                <div key={term.slug} className="wiki-term ut-paper r2">
                  <div className="wiki-term-head">
                    <span className="wiki-term-sv">{term[`term_${lang}`] || term.slug}</span>
                    <span className="wiki-term-other">{term[lang === 'sv' ? 'term_en' : 'term_sv']}</span>
                  </div>
                  <p className="wiki-term-def">{term[`def_${lang}`]}</p>
                </div>
              ))}
            </div>
          )
        ) : tab === 'documents' ? (
          // Documents tab (admin only)
          docs.length === 0 ? (
            <p className="wiki-muted">{t.emptyDocs}</p>
          ) : (
            <ul className="wiki-doclist">
              {docs.map(d => {
                const notes = d.notes || [];
                const counts = [
                  d.pagesTouched != null ? `${d.pagesTouched} ${t.pagesShort}` : null,
                  d.termsTouched != null ? `${d.termsTouched} ${t.termsShort}` : null,
                ].filter(Boolean).join(' · ');
                return (
                  <li key={d.docId} className={`status-${d.status}`}>
                    <div className="wiki-doc-row">
                      <span className="wiki-doc-name">{d.filename}</span>
                      {counts && <span className="wiki-doc-counts">{counts}</span>}
                      <button
                        className="wiki-doc-view"
                        onClick={() => openDocument(d.docId, d.filename)}
                        disabled={viewerLoading}
                        data-tip="wiki.view"
                      >
                        {t.view}
                      </button>
                      <span className="wiki-doc-status">{statusLabel(d.status)}</span>
                    </div>
                    {d.status === 'error' && d.errorMessage && (
                      <p className="wiki-doc-error">{d.errorMessage}</p>
                    )}
                    {notes.length > 0 && (
                      <details className="wiki-doc-notes">
                        <summary>{t.notesLabel} ({notes.length})</summary>
                        <ul>{notes.map((n, i) => <li key={i}>{String(n)}</li>)}</ul>
                      </details>
                    )}
                  </li>
                );
              })}
            </ul>
          )
        ) : null}

        {/* The chat stays MOUNTED and is merely hidden when another tab is
            active. Rendering it inside the conditional above unmounted it on
            every tab switch — so following a citation to a wiki page threw the
            whole conversation away. Hiding preserves messages, the
            conversation id, the draft input and the scroll position. */}
        <div className="wc-wrap" style={{ display: tab === 'chat' ? 'block' : 'none' }}>
          <WikiChat
            lang={lang}
            onOpenDoc={(docId, filename, page) => openDocument(docId, filename, page)}
            onOpenWikiPage={(slug) => { setTab('wiki'); setActiveSlug(slug); }}
          />
        </div>
      </div>

      {showAdd && (
        <AddDocumentModal
          lang={lang}
          onClose={() => setShowAdd(false)}
          onStarted={(docId) => { setShowAdd(false); setTab('documents'); setPollId(docId); fetchDocs(); }}
        />
      )}

      {viewerFile && (
        <Suspense fallback={null}>
          <DocViewerModal
            file={viewerFile}
            initialPage={viewerFile.page}
            onClose={() => setViewerFile(null)}
          />
        </Suspense>
      )}
    </>
  );
};

// --- Admin-only upload/compile modal ---
const AddDocumentModal = ({ lang, onClose, onStarted }) => {
  const t = COPY[lang];
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !uploading) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, uploading]);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError('');
    setProgress(t.uploading);
    try {
      const contentType = file.name.toLowerCase().endsWith('.zip') ? 'application/zip' : 'application/pdf';
      const up = await fetch(`${API_BASE_URL}/wiki/upload`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name, contentType }),
      });
      if (!up.ok) throw new Error('upload init failed');
      const { docId, uploadUrl } = await up.json();

      const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file });
      if (!put.ok) throw new Error('S3 upload failed');

      const start = await fetch(`${API_BASE_URL}/wiki/start`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ docId }),
      });
      if (!start.ok) throw new Error('start failed');

      onStarted(docId);
    } catch (err) {
      setError(String(err.message || err));
      setProgress('');
      setUploading(false);
    }
  };

  return (
    <div className="wiki-modal-overlay" onClick={() => !uploading && onClose()} role="dialog" aria-modal="true" data-tip-lang={lang}>
      <div className="wiki-modal-card r1" onClick={(e) => e.stopPropagation()}>
        <button className="wiki-modal-close" onClick={onClose} aria-label={t.close} disabled={uploading} data-tip="wiki.modal.close">×</button>
        <div className="ut-eyebrow">{t.eyebrow}</div>
        <h2 className="wiki-modal-title">{t.add}</h2>
        <label className="wiki-file" data-tip="wiki.modal.file">
          {file ? file.name : t.drop}
          <input type="file" accept=".pdf,.zip" hidden
                 onChange={(e) => setFile(e.target.files[0])} />
        </label>
        {error && <p className="wiki-doc-error">{error}</p>}
        <button className="wiki-upload-btn" disabled={!file || uploading} onClick={handleUpload} data-tip="wiki.modal.upload">
          {uploading ? (progress || t.uploading) : t.uploadBtn}
        </button>
      </div>
    </div>
  );
};

export default UrbanWiki;
